import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Fee,
  FeeManagerConfig,
  FeeManagerFetchInterface,
  FeeManagerFetchWriteInterface,
  GetFeesResponse,
  SaveConfigResponse,
  SaveFeesResponse
} from "@dealer-center/ddc-pages-fee-manager-interface";
import { Locale } from "@dealer-center/ddc-core-utils";

const queryKeyBase = "ddc-pages-fee-manager";

// GET hooks

export const useFees = ({ accountId, disabled }: FeeManagerFetchInterface) => {
  return useQuery({
    queryKey: [queryKeyBase, "fees", accountId],
    queryFn: () => getAllFees(accountId),
    enabled: !disabled && !!accountId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useVersion = () => {
  return useQuery({
    queryKey: [queryKeyBase, "version"],
    queryFn: getVersion,
    retry: 1,
    staleTime: Infinity
  });
};

export const useGetConfig = ({
  accountId,
  disabled
}: FeeManagerFetchInterface) => {
  return useQuery({
    queryKey: [queryKeyBase, "config", accountId],
    queryFn: () => getConfig(accountId),
    enabled: !disabled && !!accountId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useSaveConfig = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      config,
      userId
    }: {
      config: FeeManagerConfig;
      userId: string;
    }) => saveConfig(accountId, config, userId),
    onSuccess: data => {
      if (data.success) {
        queryClient.setQueryData(
          [queryKeyBase, "config", accountId],
          data.config
        );
      }

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveConfig] mutation failed:", error);
    }
  });
};

// Mutation hooks

export const useSaveFees = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fees, userId }: { fees: Fee[]; userId: string }) =>
      saveAllFees(accountId, fees, userId),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveFees] mutation failed:", error);
    }
  });
};

// API functions

const getConfig = async (accountId: string): Promise<FeeManagerConfig> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/config?accountId=${accountId}`,
    { headers: { accept: "application/json" } }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getVersion = async (): Promise<{ version: string }> => {
  const res = await fetch(`/landing/nxlambda/feeManager/version`);
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getAllFees = async (accountId: string): Promise<GetFeesResponse> => {
  const locale = Locale.getLocale();
  const res = await fetch(
    `/landing/nxlambda/feeManager/fees?accountId=${accountId}`,
    {
      headers: {
        "Accept-Language": locale
      }
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveConfig = async (
  accountId: string,
  config: FeeManagerConfig,
  userId: string
): Promise<SaveConfigResponse> => {
  const res = await fetch(`/landing/nxlambda/feeManager/config`, {
    body: JSON.stringify({ accountId, config, userId }),
    headers: { "Content-Type": "application/json" },
    method: "POST"
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveAllFees = async (
  accountId: string,
  fees: Fee[],
  userId: string
): Promise<SaveFeesResponse> => {
  const locale = Locale.getLocale();
  const res = await fetch(`/landing/nxlambda/feeManager/fees`, {
    body: JSON.stringify({ accountId, fees, userId }),
    headers: {
      "Accept-Language": locale,
      "Content-Type": "application/json"
    },
    method: "POST"
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};
